import { useQuery } from '@tanstack/react-query';
import { Printer, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { equipmentApi } from '@/api';
import { formatDate } from '@/lib/utils';
import type { EquipmentReportRepair } from '@/types';

function formatCents(cents: number | null | undefined): string {
  if (cents == null) return '—';
  return `$${(cents / 100).toFixed(2)}`;
}

function repairPartsCost(repair: EquipmentReportRepair): number {
  return repair.parts.reduce((sum, p) => {
    const cost = p.actualPrice ?? p.quotedPrice ?? 0;
    return sum + cost;
  }, 0);
}

export default function EquipmentReport({
  equipmentId,
  onClose,
}: {
  equipmentId: number;
  onClose: () => void;
}) {
  const { data, isLoading } = useQuery({
    queryKey: ['equipmentReport', equipmentId],
    queryFn: () => equipmentApi.getReport(equipmentId),
  });

  if (isLoading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80">
        <div className="animate-pulse text-muted-foreground">Loading report…</div>
      </div>
    );
  }

  if (!data) return null;

  const { equipment, repairs, pmCompletions } = data;

  const grandTotal = repairs.reduce((sum, r) => sum + repairPartsCost(r), 0);

  return (
    <div className="fixed inset-0 z-50 overflow-auto bg-background print:static print:overflow-visible">
      {/* Toolbar — hidden when printing */}
      <div className="print:hidden sticky top-0 z-10 flex items-center justify-between border-b border-border bg-background px-6 py-3">
        <h1 className="text-lg font-semibold">Equipment History Report</h1>
        <div className="flex gap-2">
          <Button size="sm" onClick={() => window.print()}>
            <Printer className="mr-2 h-4 w-4" />
            Print / Save PDF
          </Button>
          <Button size="sm" variant="outline" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Report content */}
      <div className="mx-auto max-w-4xl px-6 py-8 print:max-w-none print:px-0 print:py-4">

        {/* Equipment header */}
        <div className="mb-6 rounded-lg border border-border p-4 print:border-gray-300">
          <h2 className="text-xl font-bold mb-3">{equipment.unitNumber} — Equipment History</h2>
          <div className="grid grid-cols-2 gap-x-8 gap-y-1.5 text-sm">
            <div><span className="text-muted-foreground">Type:</span> <span className="font-medium ml-1">{equipment.equipmentType}</span></div>
            <div><span className="text-muted-foreground">Company:</span> <span className="font-medium ml-1">{equipment.company ?? '—'}</span></div>
            <div><span className="text-muted-foreground">VIN / Serial:</span> <span className="font-mono ml-1">{equipment.vinSerial ?? '—'}</span></div>
            <div><span className="text-muted-foreground">Description:</span> <span className="font-medium ml-1">{equipment.description ?? '—'}</span></div>
            {equipment.dateInService && (
              <div><span className="text-muted-foreground">Date In Service:</span> <span className="font-medium ml-1">{formatDate(equipment.dateInService)}</span></div>
            )}
            <div><span className="text-muted-foreground">Status:</span> <span className="font-medium ml-1">{equipment.assetStatus}</span></div>
          </div>
        </div>

        {/* Repairs section */}
        <h3 className="text-base font-semibold mb-3 mt-6">Repair History ({repairs.length})</h3>
        {repairs.length === 0 ? (
          <p className="text-sm text-muted-foreground mb-6">No repair records.</p>
        ) : (
          <div className="space-y-4 mb-6">
            {repairs.map((r) => {
              const cost = repairPartsCost(r);
              return (
                <div key={r.id} className="rounded-lg border border-border print:border-gray-300">
                  <div className="flex items-start justify-between px-4 py-2 border-b border-border print:border-gray-200 bg-muted/40">
                    <div>
                      <span className="font-medium text-sm">Request #{String(r.id).padStart(6, '0')}</span>
                      <span className="mx-2 text-muted-foreground">·</span>
                      <span className="text-sm text-muted-foreground">{formatDate(r.requestDate)}</span>
                      {r.status === 'Done' && r.mechanicRepairDate && (
                        <>
                          <span className="mx-2 text-muted-foreground">·</span>
                          <span className="text-sm text-muted-foreground">Completed {formatDate(r.mechanicRepairDate)}</span>
                        </>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-sm">
                      <span className="text-muted-foreground">{r.status}</span>
                      {cost > 0 && <span className="font-mono font-semibold">{formatCents(cost)}</span>}
                    </div>
                  </div>
                  <div className="px-4 py-2 space-y-2 text-sm">
                    <p><span className="text-muted-foreground">Problem:</span> <span className="ml-1">{r.problem}</span></p>
                    {r.mechanicName && (
                      <p><span className="text-muted-foreground">Mechanic:</span> <span className="ml-1">{r.mechanicName}</span>{r.mechanicRepairHours != null && <span className="text-muted-foreground ml-2">({r.mechanicRepairHours} hrs)</span>}</p>
                    )}
                    {r.mechanicNotes && (
                      <p><span className="text-muted-foreground">Notes:</span> <span className="ml-1">{r.mechanicNotes}</span></p>
                    )}
                    {r.parts.length > 0 && (
                      <table className="w-full text-xs mt-2 border border-border print:border-gray-300 rounded">
                        <thead>
                          <tr className="bg-muted/50 text-muted-foreground text-left">
                            <th className="px-3 py-1.5">Part / Service</th>
                            <th className="px-3 py-1.5">Date</th>
                            <th className="px-3 py-1.5">Quoted</th>
                            <th className="px-3 py-1.5">Actual</th>
                            <th className="px-3 py-1.5">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {r.parts.map((p) => (
                            <tr key={p.id} className="border-t border-border print:border-gray-200">
                              <td className="px-3 py-1.5 font-medium">{p.title}</td>
                              <td className="px-3 py-1.5 text-muted-foreground">{p.repairDate ? formatDate(p.repairDate) : '—'}</td>
                              <td className="px-3 py-1.5 font-mono">{formatCents(p.quotedPrice)}</td>
                              <td className="px-3 py-1.5 font-mono">{formatCents(p.actualPrice)}</td>
                              <td className="px-3 py-1.5">{p.returned ? 'Returned' : p.poStatus}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* PM Completions section */}
        <h3 className="text-base font-semibold mb-3 mt-6">PM History ({pmCompletions.length})</h3>
        {pmCompletions.length === 0 ? (
          <p className="text-sm text-muted-foreground mb-6">No PM records.</p>
        ) : (
          <table className="w-full text-sm border border-border print:border-gray-300 rounded-lg overflow-hidden mb-6">
            <thead>
              <tr className="bg-muted/50 text-muted-foreground text-left text-xs uppercase tracking-wide">
                <th className="px-4 py-2">PM Type</th>
                <th className="px-4 py-2">Completed Date</th>
                <th className="px-4 py-2">Reading</th>
                <th className="px-4 py-2">Notes</th>
              </tr>
            </thead>
            <tbody>
              {pmCompletions.map((pm) => (
                <tr key={pm.id} className="border-t border-border print:border-gray-200">
                  <td className="px-4 py-2 font-medium">
                    <span
                      className="inline-block w-2.5 h-2.5 rounded-full mr-1.5"
                      style={{ backgroundColor: pm.pmTypeColor }}
                    />
                    {pm.pmTypeName}
                  </td>
                  <td className="px-4 py-2">{formatDate(pm.completedDate)}</td>
                  <td className="px-4 py-2 text-muted-foreground font-mono">{pm.uomValueAtCompletion ?? '—'}</td>
                  <td className="px-4 py-2 text-muted-foreground">{pm.notes ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Grand total */}
        {grandTotal > 0 && (
          <div className="flex justify-end border-t border-border pt-3 print:border-gray-300">
            <div className="text-sm">
              <span className="text-muted-foreground mr-4">Total Repair Cost:</span>
              <span className="font-mono font-bold text-base">{formatCents(grandTotal)}</span>
            </div>
          </div>
        )}

        {/* Print footer */}
        <div className="mt-8 text-xs text-muted-foreground print:block hidden">
          Generated {new Date().toLocaleDateString()}
        </div>
      </div>
    </div>
  );
}
